<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";

// One navigable destination: a flat rail item (no parent) or a group child.
// `to` is already resolved to a router path by SideNav, which owns the
// relative-vs-absolute distinction between the two kinds of entry.
interface NavDestination {
  key: string;
  name: string;
  parentName: string;
  to: string;
  imgName?: string;
}

const props = defineProps({
  modelValue: { default: "", type: String },
  destinations: {
    default: () => [],
    type: Array as PropType<NavDestination[]>,
  },
  expanded: { default: false, type: Boolean },
});

const emit = defineEmits(["update:modelValue", "expand"]);

const router = useRouter();
const rootEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLInputElement | null>(null);
const activeIndex = ref(0);

const query = computed(() => props.modelValue.trim().toLowerCase());

// Children also match on their group's name so that typing "crm" surfaces the
// whole CRM section rather than only the child literally called "CRM".
const results = computed(() => {
  if (!query.value) return [];
  return props.destinations.filter(
    (destination) =>
      destination.name.toLowerCase().includes(query.value) ||
      destination.parentName.toLowerCase().includes(query.value),
  );
});

const showResults = computed(() => props.expanded && query.value.length > 0);

watch(results, () => {
  activeIndex.value = 0;
});

const setQuery = (event: Event) => {
  emit("update:modelValue", (event.target as HTMLInputElement).value);
};

// The mobile "More" sheet renders a second SideNav, so this component can be
// mounted twice. Only the instance that is actually on screen may answer the
// shortcut — a display:none element reports no client rects.
const isVisible = () => (rootEl.value?.getClientRects().length ?? 0) > 0;

// Focusing is normally enough to expand the panel — SideNav opens on focusin —
// but a zero-width focused input that failed to expand would be unusable, so
// the request is made explicitly as well. It has to come after focus(), which
// dispatches focusin synchronously: SideNav decides from that event whether the
// focus was a keyboard one, and this call is the deliberate override.
const focusInput = () => {
  inputEl.value?.focus({ preventScroll: true });
  emit("expand");
};

// Blurring is not optional: the row that was just clicked is unmounted the
// moment the query clears, and focus lost that way does not reliably emit
// focusout — which would leave SideNav believing the panel still holds focus
// and pin it open.
const reset = () => {
  emit("update:modelValue", "");
  activeIndex.value = 0;
  (document.activeElement as HTMLElement | null)?.blur?.();
};

const go = (destination: NavDestination) => {
  router.push(destination.to);
  reset();
};

const activate = () => {
  const chosen = results.value[activeIndex.value] ?? results.value[0];
  if (chosen) go(chosen);
};

const move = async (step: number) => {
  if (!results.value.length) return;
  const next = activeIndex.value + step;
  activeIndex.value = Math.min(Math.max(next, 0), results.value.length - 1);

  /*
   * The results share the rail's scroll area, and a loose query can match most
   * of the menu. Without this, arrowing down walks the highlight off the bottom
   * and Enter navigates somewhere the person never saw.
   */
  await nextTick();
  rootEl.value
    ?.querySelectorAll(".search-result")
    [activeIndex.value]?.scrollIntoView({ block: "nearest" });
};

const isMac = computed(
  () =>
    typeof navigator !== "undefined" &&
    /mac|iphone|ipad|ipod/i.test(navigator.userAgent),
);
const shortcutHint = computed(() => (isMac.value ? "⌘K" : "Ctrl K"));

/*
 * Cmd+K on a Mac, Ctrl+K everywhere else — not either-or.
 *
 * Control-K is kill-to-end-of-line in every text field on macOS, so accepting
 * ctrlKey there swallowed a system editing key app-wide: putting the caret
 * mid-line in the help-desk reply box and pressing it did nothing at all.
 *
 * The app's own global handler is not in the way: it bails unless Alt is held
 * or the key is Escape.
 */
const handleShortcut = (event: KeyboardEvent) => {
  if (event.key?.toLowerCase() !== "k") return;
  if (isMac.value ? !event.metaKey : !event.ctrlKey) return;
  if (!isVisible()) return;
  event.preventDefault();
  focusInput();
};

onMounted(() => {
  document.addEventListener("keydown", handleShortcut);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleShortcut);
});
</script>

<template>
  <div ref="rootEl" class="side-nav-search">
    <div class="search-row" :class="{ 'is-expanded': props.expanded }">
      <div class="side-icon search-box" @click="focusInput">
        <svg
          class="search-glyph"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          ref="inputEl"
          :value="props.modelValue"
          class="search-input"
          type="text"
          placeholder="Quick Search"
          aria-label="Quick search the menu"
          autocomplete="off"
          spellcheck="false"
          @input="setQuery"
          @keydown.enter.prevent="activate"
          @keydown.esc.stop.prevent="reset()"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
        />
        <span class="search-hint">{{ shortcutHint }}</span>
      </div>
    </div>

    <div v-if="showResults" class="search-results">
      <NuxtLink
        v-for="(result, index) in results"
        :key="result.key"
        :to="result.to"
        class="search-result"
        :class="{ 'is-active': index === activeIndex }"
        @mouseenter="activeIndex = index"
        @click="reset()"
      >
        <img
          v-if="result.imgName"
          :src="`/svgs/sidebar/${result.imgName}.svg`"
          class="side-icon-img shrink-0"
          alt=""
        />
        <span class="search-result-text">
          <span class="search-result-name">{{ result.name }}</span>
          <span v-if="result.parentName" class="search-result-parent">
            {{ result.parentName }}
          </span>
        </span>
      </NuxtLink>
      <div v-if="!results.length" class="search-empty">
        No matches for “{{ props.modelValue.trim() }}”
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Quick search belongs to the desktop panel only; hiding it here rather than
   with a utility class on the tag keeps the rule unambiguous and lets the
   mobile "More" sheet render the same SideNav untouched. */
.side-nav-search {
  display: none;
}

@media (min-width: 640px) {
  .side-nav-search {
    display: block;
  }
}

.search-row {
  display: flex;
  justify-content: center;
}

.search-row.is-expanded {
  justify-content: flex-start;
}

/* Collapsed the box is the same icon square as every rail button, so the rail
   reads as one column; expanded it stretches into a real search field. */
.search-box {
  flex: 0 0 auto;
  align-items: center;
  gap: 0;
  overflow: hidden;
  cursor: text;
  transition:
    flex-grow 200ms ease,
    background-color 150ms ease;
}

.search-row.is-expanded .search-box {
  flex: 1 1 auto;
  gap: 0.5rem;
}

.search-box:focus-within {
  border-color: rgb(255 255 255 / 0.45);
  background-color: rgb(255 255 255 / 0.12);
}

.search-glyph {
  width: 18px;
  height: 18px;
  flex: none;
  color: rgb(255 255 255 / 0.75);
}

/* Kept in the DOM (never display:none) while collapsed so Cmd/Ctrl+K can focus
   it — the focus is what expands the panel. */
.search-input {
  flex: 0 0 0px;
  width: 0;
  min-width: 0;
  opacity: 0;
  border: 0;
  background: transparent;
  outline: none;
  color: #fff;
  font-size: 0.875rem;
  line-height: 1.25rem;
  pointer-events: none;
  transition:
    flex-grow 200ms ease,
    opacity 150ms ease;
}

.search-row.is-expanded .search-input {
  flex: 1 1 auto;
  opacity: 1;
  pointer-events: auto;
}

/*
 * !important is not decoration here. main.css sets a global
 * `::placeholder { color: var(--placeholder-color) !important }`, and that
 * colour is rgba(86, 91, 122, .7) — a dark slate meant for placeholders on
 * white fields. On the purple rail it came out all but invisible, and nothing
 * short of matching that !important could reach it. Raised to .72 as well:
 * .5 white on this background was faint even once it was winning.
 */
.search-input::placeholder {
  color: rgb(255 255 255 / 0.72) !important;
  opacity: 1 !important;
}

.search-hint {
  flex: none;
  width: 0;
  overflow: hidden;
  opacity: 0;
  white-space: nowrap;
  border-radius: 0.375rem;
  border: 1px solid rgb(255 255 255 / 0.2);
  color: rgb(255 255 255 / 0.75);
  font-size: 0.6875rem;
  line-height: 1;
  transition:
    opacity 150ms ease,
    width 200ms ease;
}

.search-row.is-expanded .search-hint {
  width: auto;
  padding: 0.25rem 0.375rem;
  opacity: 1;
}

.search-results {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.search-result {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  border-radius: 0.625rem;
  padding: 0.5rem 0.625rem;
  color: rgb(255 255 255 / 0.85);
  transition: background-color 120ms ease;
}

.search-result.is-active,
.search-result:hover {
  background-color: rgb(255 255 255 / 0.12);
  color: #fff;
}

.search-result-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.search-result-name {
  font-size: 0.875rem;
  line-height: 1.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-result-parent {
  font-size: 0.6875rem;
  line-height: 1rem;
  color: rgb(255 255 255 / 0.55);
}

.search-empty {
  padding: 0.625rem;
  font-size: 0.8125rem;
  color: rgb(255 255 255 / 0.6);
}
</style>
